import { Injectable } from '@angular/core';
import { BigQueryService } from '../google-apis/bigquery/bigquery.service';
import { IQueryService } from './iquery.service';
import { QueryResult } from '../query-result';
import { FireService } from '../fire.service';
import { mergeSchemaWithRows_ } from '../google-apis/bigquery/utils/schema-utils';
import { AuthService } from '../auth.service';

/**
 * Max number of parallel requests to BigQuery API in single runQuery(string).
 */
const parallelRequestsCount = 8;

/**
 *  Implementation of {@link IQueryService} that uses {@link BigQueryService} and calls directly BigQuery REST API to run queries.
 */
@Injectable({
    providedIn: 'root',
})
export class DirectQueryService implements IQueryService {
    private userLabel: string = 'unknown';

    constructor(
        private bigQuery: BigQueryService,
        private fire: FireService,
        auth: AuthService
    ) {
        auth.listenState().subscribe((state) => {
            this.userLabel =
                state == null
                    ? 'unknown'
                    : state.user
                          .email!.split('@')[0]
                          .toLowerCase()
                          .replace(/\W/g, '_');
        });
    }

    async runQuery(query: string): Promise<QueryResult> {
        try {
            // execute query, wait for completion and get result rows
            const queryResponse = await this.bigQuery.jobsQuery(
                this.fire.app.options.projectId!,
                {
                    query: query,
                    useLegacySql: false,
                    // max 50gb billed ~ 0.25$
                    // maximumBytesBilled: String(1024 * 1024 * 1024 * 50),
                    // max 100gb billed ~ 0.50$
                    maximumBytesBilled: String(1024 * 1024 * 1024 * 100),
                    // 100k rows
                    maxResults: 100 * 1000,
                    // 2 minutes
                    timeoutMs: 2 * 60 * 1000,
                    labels: {
                        system: 'biviz',
                        /**
                         * Version of labels schema. Increment with every change of labels schema.
                         */
                        version: '2',
                        /**
                         * For 'inazwisko@artifexmundi.com' it will be 'inazwisko'.
                         */
                        user: this.userLabel,
                        // TODO add 'dashboard' label
                    },
                },
                {
                    prettyPrint: false,
                }
            );

            // check if query completed
            if (queryResponse.jobComplete !== true) {
                throw new Error('Job did not complete within timeout.');
            }

            // parse results
            let resultRows = mergeSchemaWithRows_(
                queryResponse.schema,
                queryResponse.rows,
                {
                    wrapIntegers: false,
                }
            );

            // check if there are more results
            // API calls return only limited data size
            if (queryResponse.pageToken != null) {
                // run in parallel max X API calls
                const pageSize = Math.ceil(
                    (queryResponse.totalRows - queryResponse.rows.length) /
                        parallelRequestsCount
                );
                const pagePromises: Promise<any[]>[] = [];
                for (
                    let startIndex = queryResponse.rows.length;
                    startIndex < queryResponse.totalRows;
                    startIndex += pageSize
                ) {
                    pagePromises.push(
                        this.getPageResults(
                            queryResponse.jobReference.jobId!,
                            startIndex,
                            Math.min(
                                pageSize,
                                queryResponse.totalRows - startIndex
                            )
                        )
                    );
                }

                // wait and process all pages
                const allResults = await Promise.all(pagePromises);
                resultRows = resultRows.concat(...allResults);
            }

            // verify rows count
            if (Number(queryResponse.totalRows) !== resultRows.length) {
                throw new Error(
                    `Expected ${queryResponse.totalRows} total rows, but got ${resultRows.length}.`
                );
            }

            return {
                status: 'ok',
                result: resultRows,
            };
        } catch (error) {
            return {
                status: 'error',
                error: error,
            };
        }
    }

    private async getPageResults(
        jobId: string,
        startIndex: number,
        pageSize: number
    ): Promise<any[]> {
        const baseResponse = await this.bigQuery.jobsGetQueryResults(
            this.fire.app.options.projectId!,
            jobId,
            {
                startIndex: startIndex,
                maxResults: pageSize,
            },
            { prettyPrint: false }
        );

        // process results
        const resultRows = mergeSchemaWithRows_(
            baseResponse.schema,
            baseResponse.rows,
            {
                wrapIntegers: false,
            }
        );

        // check if got all required results
        if (baseResponse.rows.length < pageSize) {
            // get rest of results
            while (resultRows.length < pageSize) {
                // run next page request
                const extraResponse = await this.bigQuery.jobsGetQueryResults(
                    this.fire.app.options.projectId!,
                    jobId,
                    {
                        startIndex: startIndex + resultRows.length,
                        maxResults: pageSize - resultRows.length,
                    },
                    { prettyPrint: false }
                );
                resultRows.push(
                    ...mergeSchemaWithRows_(
                        extraResponse.schema,
                        extraResponse.rows,
                        {
                            wrapIntegers: false,
                        }
                    )
                );
            }
        }

        return resultRows;
    }
}
